import React, { useCallback, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config/apiConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { Button } from '../../../components/ui/Button';
import { Switch } from '../../../components/ui/Switch';
import { cn } from '../../../lib/utils';
import { DocumentTextIcon, InfoIcon, UploadCloudIcon, XIcon } from '../../../components/ui/Icons';

type ImportResult = {
  totalRows: number;
  created: number;
  updated: number;
  linkedManagement: number;
  skipped: number;
};

const ACCEPT = '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function isAcceptableFile(file: File): boolean {
  const n = file.name.toLowerCase();
  if (n.endsWith('.xls') || n.endsWith('.xlsx')) return true;
  const m = (file.type || '').toLowerCase();
  return m.includes('spreadsheet') || m.includes('excel');
}

async function runCompaniesImport(file: File, deleteExisting: boolean): Promise<ImportResult> {
  const token = localStorage.getItem('access_token');
  const body = new FormData();
  body.append('file', file);
  body.append('deleteExisting', deleteExisting ? 'true' : 'false');

  const res = await fetch(`${API_BASE_URL}/acc-companies/import`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { message?: string | string[] } | null;
    const msg = Array.isArray(err?.message) ? err.message.join(', ') : err?.message;
    throw new Error(msg || `Import failed (${res.status})`);
  }

  return res.json() as Promise<ImportResult>;
}

const CompaniesImportPage: React.FC = () => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();

  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [deleteExisting, setDeleteExisting] = useState(false);

  const [uploadDragActive, setUploadDragActive] = useState(false);
  const uploadDragDepth = useRef(0);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);

  const clearFile = useCallback(() => {
    setFile(null);
    if (uploadFileInputRef.current) uploadFileInputRef.current.value = '';
    setResult(null);
  }, []);

  const assignFile = useCallback(
    (f: File | null) => {
      if (!f) return;
      if (isAcceptableFile(f)) {
        setFile(f);
        setResult(null);
      } else {
        showError('Use an Excel file (.xls or .xlsx) with NAME and CODE columns.');
      }
    },
    [showError],
  );

  const onUploadDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    uploadDragDepth.current += 1;
    const typeList = Array.from(e.dataTransfer?.types ?? []);
    if (typeList.includes('Files')) setUploadDragActive(true);
  }, []);

  const onUploadDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    uploadDragDepth.current -= 1;
    if (uploadDragDepth.current <= 0) {
      uploadDragDepth.current = 0;
      setUploadDragActive(false);
    }
  }, []);

  const onUploadDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onUploadDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      uploadDragDepth.current = 0;
      setUploadDragActive(false);
      const dropped = e.dataTransfer.files?.[0];
      assignFile(dropped ?? null);
    },
    [assignFile],
  );

  const openFilePicker = useCallback(() => {
    uploadFileInputRef.current?.click();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      showError('Drop a file above or use Browse files to choose your companies workbook.');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const data = await runCompaniesImport(file, deleteExisting);
      setResult(data);
      showSuccess(
        `Import finished: ${data.created} created, ${data.updated} updated, ${data.linkedManagement} vessel links (${data.totalRows} rows).`,
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Import failed.';
      showError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn('min-h-[60vh] w-full max-w-3xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 py-8 space-y-10', colors.background)}>
      <header className={cn('relative overflow-hidden rounded-2xl border shadow-sm', colors.border, colors.surface)}>
        <div className="h-1.5 w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" aria-hidden />
        <div className="p-6 sm:p-8">
          <div className="flex gap-4 max-w-2xl">
            <div className={cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-xl', 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 ring-1 ring-indigo-500/20')}>
              <DocumentTextIcon className="w-6 h-6" aria-hidden />
            </div>
            <div>
              <p className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>Administration · Import</p>
              <h1 className={cn('mt-1 text-2xl sm:text-3xl font-bold tracking-tight', colors.text)}>Companies import</h1>
              <p className={cn('mt-2 text-sm leading-relaxed', colors.textSecondary)}>
                Upload an Excel file with columns <strong className={cn('font-medium', colors.text)}>NAME</strong> and{' '}
                <strong className={cn('font-medium', colors.text)}>CODE</strong>. Rows starting with{' '}
                <code className="text-xs">MNG </code> are imported as management companies; matching vessel rows are linked automatically.
              </p>
            </div>
          </div>
        </div>
      </header>

      <form onSubmit={onSubmit} className="space-y-0">
        <input
          ref={uploadFileInputRef}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          tabIndex={-1}
          onChange={(e) => {
            const f = e.target.files?.[0];
            assignFile(f ?? null);
            e.target.value = '';
          }}
        />

        <div className={cn('rounded-2xl border shadow-md overflow-hidden', colors.border, colors.surface)}>
          <div className={cn('flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between px-5 py-4 border-b', colors.border, colors.surfaceHover)}>
            <div className="flex items-start gap-3">
              <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400')}>
                <UploadCloudIcon className="w-5 h-5" aria-hidden />
              </div>
              <div>
                <p className={cn('text-base font-semibold', colors.text)}>Import file</p>
                <p className={cn('text-sm mt-0.5 max-w-xl', colors.textSecondary)}>
                  Safe to re-run — existing companies are matched by code.
                </p>
              </div>
            </div>
          </div>

          <div className={cn('px-5 py-4 border-b', colors.border, colors.surfaceHover)}>
            <Switch
              checked={deleteExisting}
              disabled={busy}
              onChange={(e) => setDeleteExisting(e.target.checked)}
              label="Delete unused companies before import"
              description="Companies referenced in journal lines are kept; only unused rows are removed."
              labelPosition="right"
            />
          </div>

          <div
            role="button"
            tabIndex={0}
            aria-label="Drop zone for companies Excel file"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (!busy) openFilePicker();
              }
            }}
            onDragEnter={onUploadDragEnter}
            onDragLeave={onUploadDragLeave}
            onDragOver={onUploadDragOver}
            onDrop={onUploadDrop}
            onClick={(e) => {
              if (busy) return;
              if ((e.target as HTMLElement).closest('[data-no-dropzone-click]')) return;
              openFilePicker();
            }}
            className={cn(
              'relative min-h-[220px] px-6 py-10 cursor-pointer transition-all duration-200',
              busy && 'pointer-events-none opacity-60',
              uploadDragActive
                ? 'ring-2 ring-inset ring-indigo-500 border-indigo-500/40 dark:ring-indigo-400 scale-[1.002]'
                : cn('border-2 border-dashed border-transparent', 'hover:border-indigo-400/50 dark:hover:border-indigo-500/40'),
            )}
          >
            <div className="relative flex flex-col items-center justify-center text-center gap-3 max-w-lg mx-auto">
              <div className={cn('flex h-16 w-16 items-center justify-center rounded-2xl', uploadDragActive ? 'bg-indigo-500/25 text-indigo-700 dark:text-indigo-200 scale-110' : 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400')}>
                <UploadCloudIcon className="w-9 h-9" aria-hidden />
              </div>
              <p className={cn('text-lg font-semibold', colors.text)}>{uploadDragActive ? 'Release to select file' : 'Drop companies file here'}</p>
              <p className={cn('text-sm mt-1', colors.textSecondary)}>
                <button
                  type="button"
                  data-no-dropzone-click
                  onClick={(ev) => {
                    ev.stopPropagation();
                    if (!busy) openFilePicker();
                  }}
                  className={cn('font-semibold underline decoration-indigo-500/60 underline-offset-2', colors.primaryFg)}
                >
                  Browse files
                </button>
                <span className="mx-1.5 opacity-50">·</span>
                <span>.xls, .xlsx</span>
              </p>
            </div>
          </div>

          <div className={cn('flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-5 py-4 border-t', colors.border, colors.surfaceHover)}>
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <DocumentTextIcon className="w-5 h-5 shrink-0 text-indigo-600/80 dark:text-indigo-400/90" aria-hidden />
              <div className="min-w-0">
                {file ? (
                  <>
                    <p className={cn('text-sm font-medium truncate', colors.text)} title={file.name}>
                      {file.name}
                    </p>
                    <p className={cn('text-xs', colors.textMuted)}>{formatBytes(file.size)}</p>
                  </>
                ) : (
                  <p className={cn('text-sm', colors.textMuted)}>No file selected yet.</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 justify-end flex-wrap">
              {file && (
                <Button type="button" variant="ghost" size="sm" data-no-dropzone-click onClick={clearFile} disabled={busy} className="inline-flex items-center gap-1.5">
                  <XIcon className="w-4 h-4" aria-hidden />
                  Clear
                </Button>
              )}
              <Button type="submit" data-no-dropzone-click loading={busy} disabled={busy || !file} className="inline-flex items-center justify-center gap-2 px-6 rounded-xl shadow-sm">
                <UploadCloudIcon className="w-4 h-4 shrink-0" aria-hidden />
                {busy ? 'Importing…' : 'Run import'}
              </Button>
            </div>
          </div>
        </div>
      </form>

      {result && (
        <section className={cn('rounded-2xl border shadow-sm overflow-hidden', colors.border, colors.surface)}>
          <div className={cn('px-5 py-4 border-b', colors.border, colors.surfaceHover)}>
            <h2 className={cn('text-base font-semibold', colors.text)}>Last import result</h2>
          </div>
          <div className="px-5 py-4">
            <ul className={cn('grid gap-3 sm:grid-cols-2 text-sm', colors.textSecondary)}>
              {[
                ['Rows read', result.totalRows],
                ['Created', result.created],
                ['Updated', result.updated],
                ['Vessel links', result.linkedManagement],
                ['Used in journals (name only)', result.skipped],
              ].map(([label, value]) => (
                <li key={String(label)} className={cn('rounded-xl border px-4 py-3', colors.border, colors.surfaceHover)}>
                  <span className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>{label}</span>
                  <p className={cn('mt-1 text-lg font-semibold tabular-nums', colors.text)}>{value}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <p className={cn('text-xs flex flex-wrap items-start gap-2 max-w-3xl', colors.textMuted)}>
        <InfoIcon className="w-4 h-4 shrink-0 mt-0.5 opacity-70" aria-hidden />
        <span>
          Example: <code className="text-xs">LIANA</code> / <code className="text-xs">103</code> becomes a vessel when{' '}
          <code className="text-xs">MNG LIANA</code> exists in the same file; management rows are detected by the <code className="text-xs">MNG </code> prefix.
        </span>
      </p>
    </div>
  );
};

export default CompaniesImportPage;
